import { useState } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useStore } from '../../state/zustand-store';
import { handleErrors } from '../../../utils/errors';
import { Messages } from '../../../utils/message-constants';
import { useUserSettings } from '../../../utils/use-user-settings';
import {
  getListOfBuildings,
  getResourceDefaultModel,
  getAllResourceCalendars,
  getFeatures,
  getAllResourceTemplates,
  UserKeys,
  createResourceCalendar,
  updateResourceCalendar,
} from '../../../api/users-api';
import { ResourceCalendar } from '../../../../types';
import StepResourceCalendarConfig from './form-steps/step-resource-calendar-config';
import ResourceCalendarConfigSkeleton from './form-steps/form-skeleton';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import UnsavedChangesDialog, {
  useUnsavedChanges,
} from '../../../shared/unsaved-changes-dialog';
import { useAnalytics } from '../../../shared/analytics/create-events';
import * as analytics from '../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../shared/analytics/module-list';
import {
  CalendarRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import { pagesConfig } from '../../../shared/pages-config';
import SimpleBreadcrumbsPage from '../../../shared/simple-breadcrumbs-page';
import SimpleFormPage from '../../../shared/simple-form-page';
import SharedFormStepper from '../../../shared/shared-form-stepper';
import Box from '@mui/material/Box';

const ResourceCalendarSchema = z.object({
  resourceName: z.string().min(1, 'Resource name is required.'),
  resourceCategory: z.string().min(1, 'Resource category is required.'),
  capacity: z
    .number()
    .min(1, 'Capacity must be at least 1.')
    .max(10000, 'Capacity cannot exceed 10,000.'),
  buildingId: z.string().min(1, 'Building is required.'),
  buildingName: z.string().optional(),
  floorName: z.string().min(1, 'Choose a building first.'),
  floorSection: z.string().optional(),
  resourceType: z.string().optional(),
  resourceDescription: z.string().optional(),
  userVisibleDescription: z.string().optional(),
  featureInstances: z
    .array(
      z.object({
        name: z.string(),
        etags: z.string(),
        kind: z.string(),
      })
    )
    .optional(),
  resourceId: z.string().optional(),
  generatedResourceName: z.string().optional(),
  resourceEmail: z.string().optional(),
});

export type ResourceCalendarFormValues = z.infer<typeof ResourceCalendarSchema>;

export default function ResourceCalendarsFormPage() {
  const navigate = useNavigate();
  const { changeLogRoles, calendarRoles } = useUserSettings();
  const { id } = useParams();
  const selectedRowId = id;
  const pathname = useLocation().pathname;
  const currentAction = pathname.includes('/create') ? 'create' : 'update';
  const { setSnackbar } = useStore();
  const { createEvent } = useAnalytics();
  const queryClient = useQueryClient();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [activeStep, setActiveStep] = useState(0);
  const [stepCompleted, setStepCompleted] = useState([true]);

  const { data: resourceCalendars, isLoading: calendarsLoading } = useQuery({
    queryKey: [UserKeys.ResourceCalendars],
    queryFn: getAllResourceCalendars,
    meta: {
      errorMessage: Messages.Errors.Network.Loading('resource calendars'),
    },
  });

  const { data: domainDefault, isLoading: isLoadingDomainDefault } = useQuery({
    queryKey: [UserKeys.ResourceCalendarDefaults],
    queryFn: () => getResourceDefaultModel(),
    meta: {
      errorMessage: Messages.Errors.Network.Loading('domain defaults'),
    },
  });

  const { data: buildingsList, isLoading: buildingsLoading } = useQuery({
    queryKey: [UserKeys.BuildingsList],
    queryFn: () => getListOfBuildings(),
    meta: {
      errorMessage: Messages.Errors.Network.Loading('buildings'),
    },
  });

  const { data: features } = useQuery({
    queryKey: [UserKeys.Features],
    queryFn: () => getFeatures(),
    meta: {
      errorMessage: Messages.Errors.Network.Loading('features'),
    },
  });

  const { data: templates, isLoading: templatesLoading } = useQuery({
    queryKey: [UserKeys.CalendarResourceTemplates],
    queryFn: () => getAllResourceTemplates(),
    meta: {
      errorMessage: Messages.Errors.Network.Loading('resource templates'),
    },
  });

  const selectedResourceCalendar = resourceCalendars?.find(
    (calendar: ResourceCalendar) => calendar.resourceId === selectedRowId
  );

  const { mutate, isPending } = useMutation({
    mutationFn: async (data: ResourceCalendarFormValues) => {
      switch (currentAction) {
        case 'create':
          return await createResourceCalendar(data, changeLogMessage);
        case 'update':
          if (!selectedResourceCalendar) {
            throw new Error('Selected resource calendar not found');
          }
          return await updateResourceCalendar(
            data as ResourceCalendar,
            selectedResourceCalendar,
            changeLogMessage
          );
        default:
          throw new Error('Invalid action type');
      }
    },

    onSuccess: () => {
      const successMessage =
        currentAction === 'create'
          ? 'Resource calendar created successfully!'
          : 'Resource calendar updated successfully!';

      setSnackbar({ open: true, severity: 'success', message: successMessage });
      queryClient.invalidateQueries({ queryKey: [UserKeys.ResourceCalendars] });
      setOpenChangeLogDialog(false);
      setChangeLogMessage('');
      reset();
      bypass();
      navigate(`/${pagesConfig.resourceCalendars.link}`);
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Creating(
          'this resource calendar'
        ),
      });
    },
    onSettled: () => {
      const actionKey =
        `${currentAction}_resource_calendar` as keyof typeof analytics.calendars.resource_calendars;
      createEvent(analytics.calendars.resource_calendars[actionKey], {
        module: ModuleList.modules.calendars.name,
        section: ModuleList.modules.calendars.sections.resourceCalendars,
      });
      setOpenChangeLogDialog(false);
    },
  });

  const {
    handleSubmit,
    control,
    trigger,
    reset,
    getValues,
    setValue,
    watch,
    formState: { errors, isValidating, isSubmitting, isDirty },
  } = useForm<ResourceCalendarFormValues>({
    resolver: zodResolver(ResourceCalendarSchema),
    mode: 'onBlur',
    ...(currentAction === 'update' && selectedResourceCalendar
      ? {
          values: {
            buildingName:
              selectedResourceCalendar.buildingName ??
              buildingsList?.find(
                (building) =>
                  building.buildingId === selectedResourceCalendar.buildingId
              )?.buildingName ??
              '',
            buildingId: selectedResourceCalendar.buildingId || '',
            resourceDescription:
              selectedResourceCalendar.resourceDescription ?? '',
            floorName: selectedResourceCalendar.floorName || '',
            resourceId: selectedResourceCalendar.resourceId ?? '',
            resourceName: selectedResourceCalendar.resourceName ?? '',
            resourceType: selectedResourceCalendar.resourceType ?? '',
            generatedResourceName: '',
            capacity: selectedResourceCalendar.capacity || 1,
            floorSection: selectedResourceCalendar.floorSection || '',
            resourceCategory:
              selectedResourceCalendar.resourceCategory || 'CONFERENCE_ROOM',
            userVisibleDescription:
              selectedResourceCalendar.userVisibleDescription ?? '',
            featureInstances: selectedResourceCalendar.featureInstances ?? [],
            resourceEmail: selectedResourceCalendar.resourceEmail ?? '',
          },
        }
      : currentAction === 'create' && domainDefault?.isActive
      ? {
          values: {
            buildingName:
              buildingsList?.find(
                (building) => building.buildingId === domainDefault!.buildingId
              )?.buildingName ?? '',
            buildingId: domainDefault!.buildingId || '',
            resourceDescription: '',
            floorName: domainDefault!.floorName || '',
            resourceId: '',
            resourceName: '',
            resourceType: domainDefault!.resourceType || '',
            generatedResourceName: '',
            capacity: 1,
            floorSection: domainDefault!.floorSection || '',
            resourceCategory:
              domainDefault!.resourceCategory || 'CONFERENCE_ROOM',
            userVisibleDescription: '',
            featureInstances: [],
            resourceEmail: '',
          },
        }
      : {
          defaultValues: {
            buildingName: '',
            buildingId: '',
            resourceDescription: '',
            floorName: '',
            resourceId: '',
            resourceName: '',
            resourceType: '',
            generatedResourceName: '',
            capacity: 1,
            floorSection: '',
            resourceCategory: 'CONFERENCE_ROOM',
            userVisibleDescription: '',
            featureInstances: [],
            resourceEmail: '',
          },
        }),
  });

  const {
    showDialog: showUnsavedChangesDialog,
    handleConfirm: handleUnsavedConfirm,
    handleCancel: handleUnsavedCancel,
    bypass,
    isCancelAction,
  } = useUnsavedChanges(isDirty);

  const handleCancel = () => {
    navigate(`/${pagesConfig.resourceCalendars.link}`, {
      state: { fromCancelButton: true },
    });
  };

  const formKey =
    currentAction === 'update' && selectedResourceCalendar
      ? `update-${selectedResourceCalendar.resourceId}`
      : 'create';

  const steps = [
    {
      label: 'Configuration',
      schema: ResourceCalendarSchema,
    },
  ];

  const formStepperProps = {
    activeStep,
    setActiveStep,
    steps,
    trigger,
    errors,
    stepCompleted,
    setStepCompleted,
  };

  function handleFormData(data: ResourceCalendarFormValues) {
    const resourceId =
      data.resourceId ||
      (data.resourceName ? data.resourceName.replace(/ /g, '') : '');
    return {
      ...data,
      resourceId,
      generatedResourceName: data.generatedResourceName,
      buildingName: data.buildingName?.trim() || '',
      resourceName: data.resourceName?.trim(),
      floorName: data.floorName?.trim(),
      floorSection: data.floorSection?.trim(),
      resourceType: data.resourceType?.trim(),
      userVisibleDescription: data.userVisibleDescription?.trim(),
      resourceDescription: data.resourceDescription?.trim(),
    };
  }

  const onSubmit: SubmitHandler<ResourceCalendarFormValues> = async (data) => {
    const isFormValid = await trigger();
    if (!isFormValid) return;

    const categoryChanged =
      currentAction === 'update' &&
      selectedResourceCalendar &&
      data.resourceCategory !== selectedResourceCalendar.resourceCategory;

    const needsChangelogForCategory =
      (isAbleToSeeUIElement(
        changeLogRoles,
        CalendarRoles.CanSelectCategoryUnknown.value
      ) &&
        data.resourceCategory === 'CATEGORY_UNKNOWN') ||
      (isAbleToSeeUIElement(
        changeLogRoles,
        CalendarRoles.CanSelectConferenceRoom.value
      ) &&
        data.resourceCategory === 'CONFERENCE_ROOM') ||
      (isAbleToSeeUIElement(
        changeLogRoles,
        CalendarRoles.CanSelectOther.value
      ) &&
        data.resourceCategory === 'OTHER');

    const needsChangeLog =
      (currentAction === 'create' &&
        isAbleToSeeUIElement(
          changeLogRoles,
          CalendarRoles.CreateResourceCalendar.value
        )) ||
      (currentAction === 'update' &&
        isAbleToSeeUIElement(
          changeLogRoles,
          CalendarRoles.EditResourceCalendar.value
        )) ||
      (currentAction === 'create' && needsChangelogForCategory) ||
      (categoryChanged &&
        currentAction === 'update' &&
        needsChangelogForCategory);

    if (needsChangeLog) {
      setOpenChangeLogDialog(true);
    } else {
      mutate(handleFormData(data));
    }
  };

  const isLoading =
    calendarsLoading ||
    buildingsLoading ||
    (currentAction === 'create' && isLoadingDomainDefault) ||
    (currentAction === 'create' && templatesLoading) ||
    (currentAction === 'update' && !selectedResourceCalendar) ||
    !resourceCalendars ||
    !buildingsList ||
    !features ||
    (currentAction === 'create' && !templates);

  return (
    <SimpleBreadcrumbsPage
      isSubPage
      breadcrumbs={[
        { label: ModuleList.modules.calendars.name },
        {
          label: pagesConfig.resourceCalendars.name,
          path: pagesConfig.resourceCalendars.link,
        },
      ]}
    >
      <SimpleFormPage
        key={formKey}
        title={
          currentAction === 'create'
            ? 'Create Resource Calendar'
            : 'Update Resource Calendar'
        }
        stepper={<SharedFormStepper {...formStepperProps} />}
        {...formStepperProps}
        content={
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {isLoading ? (
              <ResourceCalendarConfigSkeleton
                isEditMode={currentAction === 'update'}
              />
            ) : (
              <StepResourceCalendarConfig
                key={formKey}
                control={control}
                watch={watch}
                setValue={setValue}
                trigger={trigger}
                errors={errors}
                calendarRoles={calendarRoles}
                buildingsList={buildingsList || []}
                features={features || []}
                templates={templates || []}
                domainDefault={domainDefault}
                isEditMode={currentAction === 'update'}
                selectedCalendar={selectedResourceCalendar}
              />
            )}
          </Box>
        }
        handleDiscard={handleCancel}
        handleSubmit={handleSubmit(onSubmit)}
        submitButtonText={
          currentAction === 'create'
            ? 'Create Resource Calendar'
            : 'Update Resource Calendar'
        }
        isLoadingSubmit={isValidating || isSubmitting || isPending}
        isDisableSubmit={isLoading}
      />

      <UnsavedChangesDialog
        isCancelAction={isCancelAction}
        entityName="Resource Calendar"
        pageMode={currentAction}
        open={showUnsavedChangesDialog}
        handleConfirm={handleUnsavedConfirm}
        handleClose={handleUnsavedCancel}
      />
      <ChangeLogDialog
        open={openChangeLogDialog}
        handleClose={() => setOpenChangeLogDialog(false)}
        changeLogMessage={changeLogMessage}
        setChangeLogMessage={setChangeLogMessage}
        mutate={() =>
          mutate(handleFormData(getValues() as ResourceCalendarFormValues))
        }
        mutateLoading={isPending}
      />
    </SimpleBreadcrumbsPage>
  );
}
